import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRxCollection, useRxDatabase } from 'rxdb/plugins/react';
import type { ZodTypeAny } from 'zod';
import type { MediaOwnerType, Subject } from '@ast-data/shared';
import type { AppDatabase } from '../db/database';
import {
    applyMediaRemoval,
    mediaRemovalImpact,
    planMediaRemoval,
    type MediaRemovalImpact
} from '../features/media/ownerMediaRemoval';
import {
    useGridApiRef,
    type GridFilterModel,
    type GridSortModel
} from '../lib/dataGrid';
import { useAbility, scopedSubject } from '../features/auth/abilityContext';
import { useProjectDocs } from '../db/useScopedDocs';
import { removeById } from '../db/removeById';
import {
    useDateLinkFilterItems,
    useFindingLinkFilterItems
} from './gridDeepLink';
import { useEditorCellFocus } from './cellFocus';
import { useInlineRowUpdate, type InlineField } from './gridInlineEdit';
import { useGridBulkEdit } from './useGridBulkEdit';
import { labelIdsFrom } from './formFields';
import { removeOwnerTranslations } from '../features/translation/ownerTranslations';
import type { HistoryVersion } from '@ast-data/shared';

/** The minimum a row must be for the shared machinery to work on it. */
export interface RecordRow {
    id: string;
    projectId: string;
}

export interface RecordGridConfig<Row extends RecordRow> {
    /** The entity's definition — collection name, apiBase and history fields. */
    entity: {
        name: string;
        apiBase: string;
        collection: string;
        historyFields: readonly string[];
    };
    /** The Zod input schema the Sammelbearbeitung validates against. */
    schema: ZodTypeAny;
    /** CASL subject, scoped to the project for the create/update checks. */
    subject: Subject;
    /**
     * i18n namespace. Everything the shell says derives from it: `<prefix>.title`,
     * `.add`, `.bulkEdit.title`, `.bulkEdit.done`, `.history.title`,
     * `.delete.title`, `.delete.confirm` and `<prefix>.field.*`.
     */
    labelPrefix: string;
    /** Last segment of the record's office route, e.g. 'photos'. */
    routeSegment: string;
    /**
     * Stable id for the saved column layout. MUST be unique per grid — an office
     * grid and its Auswertung twin need separate ids.
     */
    gridId: string;
    /** The column that identifies a row: pinned, bold, sorted and double-clicked. */
    keyField: string & keyof Row;
    /**
     * A version's title in the history, where `keyField` alone does not name
     * the record — see HistoryDialog's `primaryLabel`.
     */
    historyTitle?: (version: HistoryVersion) => string;
    /**
     * Columns this grid ships hidden, as a `columnVisibilityModel`-shaped map. The
     * phone card and the cross-project Auswertung read the same map, so no two
     * views of one entity can end up showing different columns.
     */
    hiddenColumns: Record<string, boolean>;
    /**
     * Which columns get an editor, and which fields a committed cell may write.
     * Passed as the HOOK (it reads the rows) so both halves are defined once.
     */
    useInlineFields: (rows: readonly Row[]) => InlineField[];
    /**
     * How the grid is sorted before the user sorts it themselves. Defaults to the
     * key column ascending; a Tagebuch names its date instead. Read by the
     * Auswertung twin too (see {@link recordSortModel}), so the two views of one
     * entity open in the same order.
     */
    sortModel?: GridSortModel;
    /**
     * The date column `?date=` filters on when the grid is opened from the
     * Tagebuch's "was recorded today" list. Omit where the grid IS the day.
     */
    dateLinkField?: string;
    /**
     * The Befundnummer column `?finding=` filters on when the grid is opened
     * from a Befund ("in der Fundliste anzeigen"). See gridDeepLink.
     */
    findingLinkField?: string;
    /**
     * Ticks are normally offered only where the user may edit. 'always' overrides
     * that for the grids whose ticks also drive a PDF export, which a reader is as
     * entitled to.
     */
    checkboxSelection?: 'whenEditable' | 'always';
    /**
     * Set where a record carries photos and sketches of its own. Deleting it
     * then takes them along — or, where one is linked into another
     * description, leaves it there — and the confirm dialog says which.
     */
    mediaOwnerType?: MediaOwnerType;
}

/**
 * How a record grid opens: its configured sort, or the key column ascending. A
 * function rather than a hook default, because the cross-project Auswertung needs
 * the same answer without running the hook.
 */
export function recordSortModel<Row extends RecordRow>(
    config: RecordGridConfig<Row>
): GridSortModel {
    return config.sortModel ?? [{ field: config.keyField, sort: 'asc' }];
}

/**
 * Everything the five per-project record grids do the same way: read the project's
 * rows, resolve what the user may do with them, open the record editors (which are
 * child routes), edit cells inline, bulk-edit a ticked set, and hold the
 * delete/history targets.
 *
 * Pairs with {@link RecordGridView}, which renders the result. Two pieces rather
 * than one because the columns sit between them: the caller builds them from the
 * handlers here, and the view draws them.
 */
export function useRecordGrid<Row extends RecordRow>(
    config: RecordGridConfig<Row>,
    projectId: string
) {
    const {
        entity,
        schema,
        subject,
        labelPrefix,
        routeSegment,
        keyField,
        useInlineFields,
        dateLinkField,
        findingLinkField,
        checkboxSelection = 'whenEditable',
        mediaOwnerType
    } = config;

    const collection = useRxCollection<Row>(entity.collection);
    const database = useRxDatabase() as unknown as AppDatabase | null;
    // People come shown by their accounts (useProjectDocs -> usePersonLabels):
    // in the cells, and so in the filters, the quick search and the exports.
    const { results: rows, loading: loading } = useProjectDocs<Row>(
        entity.collection,
        projectId
    );

    const ability = useAbility();
    const scoped = scopedSubject(subject, { projectId });
    const canCreate = ability.can('create', scoped);
    const canEdit = ability.can('update', scoped);

    const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
    const [historyTarget, setHistoryTarget] = useState<Row | null>(null);

    // The editors are child routes — each has its own (shareable) URL.
    const navigate = useNavigate();
    const basePath = `/office/projects/${projectId}/${routeSegment}`;
    const handleAdd = useCallback(
        () => navigate(`${basePath}/new`),
        [navigate, basePath]
    );
    const handleEdit = useCallback(
        (row: Row) => navigate(`${basePath}/${row.id}/edit`),
        [navigate, basePath]
    );

    // Double-clicking the key column opens the record — read-only for a
    // reader; every other column belongs to the cell editor (see
    // useEditorCellFocus).
    const handleCellDoubleClick = useEditorCellFocus(
        keyField,
        (row) => `${basePath}/${row.id}/edit`
    );

    // What the pending delete does to the record's pictures, for the dialog.
    // Keyed by the target, so a stale answer is never shown for another row.
    const [deleteImpact, setDeleteImpact] = useState<{
        id: string;
        impact: MediaRemovalImpact;
    } | null>(null);
    useEffect(() => {
        if (!deleteTarget || !mediaOwnerType || !database) {
            return;
        }
        let cancelled = false;
        void planMediaRemoval(database, [
            { ownerType: mediaOwnerType, ownerId: deleteTarget.id }
        ]).then(({ plan, media }) => {
            if (!cancelled) {
                setDeleteImpact({
                    id: deleteTarget.id,
                    impact: mediaRemovalImpact(plan, media)
                });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [deleteTarget, mediaOwnerType, database]);

    async function confirmDelete() {
        if (deleteTarget) {
            // Planned again rather than reused: the pictures may have changed
            // while the dialog was open.
            if (mediaOwnerType && database) {
                const { plan } = await planMediaRemoval(database, [
                    { ownerType: mediaOwnerType, ownerId: deleteTarget.id }
                ]);
                await applyMediaRemoval(database, plan);
            }
            if (database) {
                await removeOwnerTranslations(database, [deleteTarget.id]);
            }
            await removeById(collection, deleteTarget.id);
        }
        setDeleteTarget(null);
    }

    const inlineFields = useInlineFields(rows as readonly Row[]);
    const processRowUpdate = useInlineRowUpdate<Row>(collection, inlineFields);

    // The same fields the cells edit, so the two can never offer different
    // things.
    const apiRef = useGridApiRef();
    const bulkEdit = useGridBulkEdit<Row>({
        apiRef,
        collection,
        fields: inlineFields,
        schema,
        canEdit,
        labelId: labelIdsFrom(`${labelPrefix}.field`),
        titleId: `${labelPrefix}.bulkEdit.title`,
        doneId: `${labelPrefix}.bulkEdit.done`,
        docOf: (row) => row
    });

    // How many rows are ticked — the PDF exports label their menu entry with it
    // and disable themselves at zero.
    const [selectedCount, setSelectedCount] = useState(0);
    const selectedIds = useCallback(
        () =>
            [...(apiRef.current?.getSelectedRows().values() ?? [])].map(
                (row) => (row as Row).id
            ),
        [apiRef]
    );
    const onSelectionChange = useCallback(() => {
        setSelectedCount(selectedIds().length);
        bulkEdit.onSelectionChange();
    }, [selectedIds, bulkEdit]);

    // `?date=` opens this grid on one day, `?finding=` on one Befund's records
    // — see gridDeepLink. Grids that are themselves a day (the Tagebuch) pass
    // no date field and get no day filter.
    const dayItems = useDateLinkFilterItems(dateLinkField ?? '');
    const findingItems = useFindingLinkFilterItems(findingLinkField);
    const linkFilter: GridFilterModel = useMemo(
        () => ({
            items: [...(dateLinkField ? dayItems : []), ...findingItems]
        }),
        [dateLinkField, dayItems, findingItems]
    );

    return {
        config,
        projectId,
        rows: rows as readonly Row[],
        loading,
        collection,
        canCreate,
        canEdit,
        showCheckboxes: checkboxSelection === 'always' || canEdit,
        apiRef,
        basePath,
        handleAdd,
        handleEdit,
        handleCellDoubleClick,
        inlineFields,
        processRowUpdate,
        bulkEdit,
        selectedIds,
        selectedCount,
        onSelectionChange,
        linkFilter,
        deleteTarget,
        setDeleteTarget,
        historyTarget,
        setHistoryTarget,
        confirmDelete,
        /** The pending delete's effect on the record's pictures, once known. */
        deleteMediaImpact:
            deleteTarget && deleteImpact?.id === deleteTarget.id
                ? deleteImpact.impact
                : null,
        /** The handlers every `use<Entity>Columns` hook takes, in one spread. */
        columnProps: {
            canEdit,
            inlineFields,
            onEdit: handleEdit,
            onDelete: setDeleteTarget,
            onHistory: setHistoryTarget
        }
    };
}

export type RecordGrid<Row extends RecordRow> = ReturnType<
    typeof useRecordGrid<Row>
>;
